import { Link } from 'react-router-dom';
import {
  ShieldCheckIcon,
  CloudIcon,
  ChartBarIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import Button from '../../components/common/Button';

const features = [
  {
    name: 'Identify Weak Authentication & Enforce MFA Compliance',
    description: 'Check MFA status, authentication methods, and conditional access policies across all users.',
    icon: ShieldCheckIcon,
  },
  {
    name: '1-Click "Fix-It-All" Remediation',
    description: 'Go from detection to resolution in seconds. Review flagged security gaps and apply verified configuration fixes across Microsoft 365 and Google Workspace with a single click. Never runs without your explicit authorization.',
    icon: WrenchScrewdriverIcon,
  },
  {
    name: 'Detect Unsecured Devices & Outdated Systems',
    description: 'Inventory enrolled devices, check compliance status, encryption, and OS versions.',
    icon: CloudIcon,
  },
  {
    name: 'Real-Time Security Risk Score',
    description: 'Visual security posture scoring with category breakdowns and trend analysis.',
    icon: ChartBarIcon,
  },
  {
    name: 'Audit-Ready Cyber Essentials Reports',
    description: 'Generate PDF reports for Cyber Essentials, GDPR, and ISO 27001 readiness.',
    icon: DocumentTextIcon,
  },
];

const platforms = [
  { name: 'Microsoft 365', description: 'Azure AD, Intune, OneDrive, SharePoint' },
  { name: 'Google Workspace', description: 'Admin SDK, Drive, Gmail, Directory' },
];

export default function HomePage() {
  return (
    <div className="bg-white dark:bg-slate-900">
      {/* Hero Section */}
      <div className="relative isolate overflow-hidden">
        <div className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80">
          <div className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-primary-200 to-primary-400 opacity-20 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]" />
        </div>

        <div className="mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h1 className="text-4xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-6xl">
              <span className="text-primary-600">CyberActions</span>
            </h1>
            <p className="mt-2 text-xl text-slate-700 dark:text-slate-200">
              Pass Cyber Essentials Faster with Automated Security Audits
            </p>
            <p className="mt-6 text-lg leading-8 text-slate-600 dark:text-slate-300">
              Instantly scan your Microsoft 365 and Google Workspace environments, identify
              compliance risks, and generate audit-ready reports in minutes.
            </p>
            <p className="mt-4 text-sm font-medium text-slate-500 dark:text-slate-400">
              Read-only scanning with one-click &quot;Fix-It-All&quot; remediation — no changes without your explicit approval. Built for UK compliance.
            </p>
            <div className="mt-10 flex items-center justify-center gap-x-6">
              <Link to="/signup">
                <Button size="lg">Start Free Audit</Button>
              </Link>
              <Link to="/pricing">
                <Button variant="outline" size="lg">View Pricing</Button>
              </Link>
            </div>

            {/* Cyber Essentials certification trust strip */}
            <div className="mt-10 flex justify-center">
              <a
                href="/cyber-essentials-certificate.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-800/70 backdrop-blur px-4 py-2 shadow-sm hover:border-primary-500 dark:hover:border-primary-400 transition-colors"
                aria-label="View Cyber Essentials Certificate (PDF)"
              >
                <img
                  src="/cyber-essentials-badge.png"
                  alt="Cyber Essentials Certified"
                  className="h-14 w-auto"
                />
                <span className="flex flex-col leading-tight text-left">
                  <span className="text-xs font-semibold text-slate-900 dark:text-white">
                    Cyber Essentials Certified ✅
                  </span>
                  <span className="text-[11px] text-slate-500 dark:text-slate-400">
                    Cyber Essentials Plus in progress
                  </span>
                </span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Platforms Section */}
      <div className="bg-slate-50 dark:bg-slate-800/50 py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-base font-semibold text-primary-600">Supported Platforms</h2>
            <p className="mt-2 text-3xl font-bold tracking-tight text-slate-900 dark:text-white">
              Connect your cloud workspace
            </p>
          </div>
          <div className="mx-auto mt-10 grid max-w-lg grid-cols-1 gap-6 sm:grid-cols-2 lg:max-w-none">
            {platforms.map((platform) => (
              <div
                key={platform.name}
                className="flex items-center gap-4 rounded-xl bg-white dark:bg-slate-800 p-6 shadow-sm border border-slate-200 dark:border-slate-700"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/30">
                  <CloudIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 dark:text-white">{platform.name}</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{platform.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div id="features" className="py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-base font-semibold text-primary-600">Features</h2>
            <p className="mt-2 text-3xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-4xl">
              Everything you need to audit your security
            </p>
            <p className="mt-6 text-lg text-slate-600 dark:text-slate-300">
              Read-only security auditing with user-controlled remediation — nothing changes without your approval.
            </p>
          </div>

          <div className="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-8 sm:mt-20 lg:max-w-none lg:grid-cols-2">
            {features.map((feature) => (
              <div
                key={feature.name}
                className="flex flex-col rounded-xl bg-white dark:bg-slate-800 p-8 shadow-sm border border-slate-200 dark:border-slate-700 hover:shadow-md transition-shadow"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/30">
                  <feature.icon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                </div>
                <h3 className="mt-6 text-lg font-semibold text-slate-900 dark:text-white">
                  {feature.name}
                </h3>
                <p className="mt-2 text-slate-600 dark:text-slate-300">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="bg-primary-600 py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-white">
              Why choose CyberActions?
            </h2>
          </div>
          <div className="mx-auto mt-10 grid max-w-lg grid-cols-1 gap-4 sm:grid-cols-2 lg:max-w-4xl lg:grid-cols-3">
            {[
              'User-Controlled Remediation — Read-only security scanning with one-click "Fix-It-All" remediation. No changes are made without your explicit approval.',
              'Multi-tenant SaaS - isolated data per organization',
              'UK compliance focused - Cyber Essentials, GDPR',
              'Simple setup - OAuth connection in minutes',
              'Evidence collection for audits',
              'Automated weekly security checks',
            ].map((benefit) => (
              <div key={benefit} className="flex items-center gap-3">
                <CheckCircleIcon className="h-6 w-6 text-primary-200 flex-shrink-0" />
                <span className="text-white">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">
              Ready to audit your security posture?
            </h2>
            <p className="mt-6 text-lg text-slate-600 dark:text-slate-300">
              Get started with a free trial. No credit card required.
            </p>
            <div className="mt-10">
              <Link to="/signup">
                <Button size="lg">Get Started Free</Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
